/* SharedHealthIntelligencePage — публичен read-only doctor view (iter420.5).
 *
 * Route: /share/health/:token (без auth wrap)
 * Извиква /api/public/health-intelligence/share/{token}
 * Показва патиent profile без login.
 *
 * Designed за бърз клиничен преглед: на 1 екран лекарят вижда всичко
 * (HI Score, conditions, recent meals trends, симптоми, latest lab biomarkers).
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import {
  Brain, Heart, Activity, Users, Flame, FlaskConical, Leaf,
  Clock, Loader2, AlertTriangle, ShieldCheck, Calendar,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { bg as bgLocale } from "date-fns/locale";

const SYMPTOM_LABELS_BG = {
  fatigue: "Умора", bloating: "Подуване", constipation: "Запек",
  diarrhea: "Диария", hair_loss: "Косопад", anxiety: "Тревожност",
  insomnia: "Безсъние", headache: "Главоболие", joint_pain: "Болки в ставите",
  acid_reflux: "Киселини", low_libido: "Ниско либидо",
  irregular_cycle: "Нередовен цикъл", acne: "Акне", brain_fog: "Мъгла в главата",
  low_energy: "Ниска енергия",
};
const RELATION_LABELS_BG = {
  mother: "Майка", father: "Баща", sibling: "Брат/сестра",
  grandparent_maternal: "Баба/дядо (м.)", grandparent_paternal: "Баба/дядо (б.)",
  child: "Дете",
};

export default function SharedHealthIntelligencePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/health-intelligence/share/${token}`);
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Линкът е невалиден или изтекъл");
      } finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md border-rose-200 bg-rose-50">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
            <h2 className="text-base font-bold text-rose-900 mb-2">Невалиден линк</h2>
            <p className="text-sm text-rose-800">{error}</p>
            <p className="text-[11px] text-rose-700 mt-3">
              Помоли пациента за нов 7-дневен read-only линк.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const meta = data.share_meta || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50/40 py-6 px-4 sm:px-6 lg:px-8"
         data-testid="shared-hi-page">
      <div className="max-w-5xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-lg">
              <Brain className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Health Intelligence</h1>
              <p className="text-xs text-slate-600">Read-only пациентски преглед</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white shadow-sm border border-slate-200">
            <Clock className="w-3.5 h-3.5 text-amber-600" />
            <span className="text-[11px] font-bold text-slate-700">
              Изтича: {format(parseISO(meta.expires_at), "dd MMM yyyy", { locale: bgLocale })}
            </span>
          </div>
        </div>

        {/* Patient banner */}
        <Card className="border-violet-200 bg-gradient-to-br from-violet-100/60 to-fuchsia-100/40">
          <CardContent className="p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-wider font-bold text-violet-700">
                  Пациент
                </p>
                <h2 className="text-lg font-bold text-slate-900 mt-0.5"
                    data-testid="shared-patient-name">{data.patient?.name}</h2>
                <p className="text-xs text-slate-600 mt-0.5">
                  {data.patient?.age && `${data.patient.age} г.`}
                  {data.patient?.gender && ` · ${data.patient.gender}`}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider font-bold text-violet-700">HI Score</p>
                <div className="text-3xl font-bold text-violet-700" data-testid="shared-hi-score">
                  {data.hi_score}
                  <span className="text-sm font-medium text-violet-500"> / 1000</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 2-col grid: Conditions + Family */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Card className="border-slate-200 bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2.5">
                <Heart className="w-4 h-4 text-rose-500" />
                <h3 className="text-sm font-bold text-slate-900">Заболявания / Състояния</h3>
                <span className="ml-auto text-[10px] font-bold text-slate-500">
                  {data.conditions?.length || 0}
                </span>
              </div>
              {(data.conditions || []).length === 0
                ? <p className="text-xs text-slate-500 py-2">Не са въведени.</p>
                : <ul className="space-y-1.5" data-testid="shared-conditions">
                    {data.conditions.map((c, i) => (
                      <li key={i} className="text-[12px] flex items-start gap-2">
                        <span className="text-rose-500 mt-0.5">•</span>
                        <div>
                          <strong className="text-slate-900">{c.name_bg}</strong>
                          {c.severity && <span className="text-[10.5px] text-slate-500 ml-1">({c.severity})</span>}
                          <span className="block text-[10.5px] text-slate-500 mt-0.5">{c.category}</span>
                        </div>
                      </li>
                    ))}
                  </ul>}
            </CardContent>
          </Card>

          <Card className="border-slate-200 bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2.5">
                <Users className="w-4 h-4 text-violet-500" />
                <h3 className="text-sm font-bold text-slate-900">Фамилна анамнеза</h3>
                <span className="ml-auto text-[10px] font-bold text-slate-500">
                  {data.family_history?.length || 0}
                </span>
              </div>
              {(data.family_history || []).length === 0
                ? <p className="text-xs text-slate-500 py-2">Не са въведени.</p>
                : <ul className="space-y-1" data-testid="shared-family">
                    {data.family_history.map((f, i) => (
                      <li key={i} className="text-[12px]">
                        <strong className="text-slate-900">{RELATION_LABELS_BG[f.relation] || f.relation}:</strong>{" "}
                        <span className="text-slate-700">{f.name_bg}</span>
                        {f.age_at_diagnosis && <span className="text-[10.5px] text-slate-500"> (на {f.age_at_diagnosis})</span>}
                      </li>
                    ))}
                  </ul>}
            </CardContent>
          </Card>
        </div>

        {/* Symptoms (last 30 days) */}
        {(data.symptoms_last_30d || []).length > 0 && (
          <Card className="border-slate-200 bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2.5">
                <Activity className="w-4 h-4 text-amber-600" />
                <h3 className="text-sm font-bold text-slate-900">Симптоми (последни 30 дни)</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="shared-symptoms">
                {data.symptoms_last_30d.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200">
                    <span className="text-[11.5px] flex-1">{SYMPTOM_LABELS_BG[s.symptom_id] || s.symptom_id}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                          s.avg_severity >= 7 ? "bg-rose-100 text-rose-700"
                                              : s.avg_severity >= 4 ? "bg-amber-100 text-amber-700"
                                                                    : "bg-emerald-100 text-emerald-700"
                        }`}>
                      ø{s.avg_severity}
                    </span>
                    <span className="text-[9px] text-slate-500">×{s.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent meals */}
        {(data.recent_meals || []).length > 0 && (
          <Card className="border-slate-200 bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2.5">
                <Flame className="w-4 h-4 text-orange-500" />
                <h3 className="text-sm font-bold text-slate-900">Последни хранения</h3>
              </div>
              <div className="space-y-1.5" data-testid="shared-meals">
                {data.recent_meals.map((m, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-slate-50">
                    <div className="flex-1 min-w-0">
                      <span className="text-[11.5px] font-bold text-slate-900 truncate block">{m.title}</span>
                      <span className="text-[10px] text-slate-500">
                        {format(parseISO(m.created_at), "dd MMM HH:mm", { locale: bgLocale })}
                      </span>
                    </div>
                    <div className="flex gap-1 text-[10px] flex-shrink-0">
                      {m.dil != null && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">DIL {Math.round(m.dil)}g</span>}
                      {m.nis_pct != null && <span className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-bold">{Math.round(m.nis_pct)}%</span>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Latest lab */}
        {data.latest_lab && (
          <Card className="border-slate-200 bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2.5">
                <FlaskConical className="w-4 h-4 text-sky-500" />
                <h3 className="text-sm font-bold text-slate-900">Последен лабораторен панел</h3>
                <span className="ml-auto text-[10px] text-slate-500">
                  {format(parseISO(data.latest_lab.created_at), "dd MMM yyyy", { locale: bgLocale })}
                </span>
              </div>
              <p className="text-[11.5px] text-slate-700 mb-2">{data.latest_lab.summary}</p>
              <div className="space-y-0.5" data-testid="shared-lab">
                {(data.latest_lab.biomarkers || []).slice(0, 12).map((b, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px] py-1 border-b border-slate-100 last:border-0">
                    <strong className="flex-1 text-slate-800">{b.name}</strong>
                    <span className="font-mono text-slate-700">{b.value} {b.unit}</span>
                    {b.reference_range && <span className="text-[10px] text-slate-500">({b.reference_range})</span>}
                    {b.flag && b.flag !== "normal" && (
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
                            b.flag === "high" ? "bg-rose-100 text-rose-700" : "bg-sky-100 text-sky-700"
                          }`}>
                        {b.flag.toUpperCase()}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Food recommendations summary */}
        {data.food_recommendations?.top?.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card className="border-emerald-200 bg-emerald-50/40">
              <CardContent className="p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <Leaf className="w-3.5 h-3.5 text-emerald-600" />
                  <h4 className="text-xs font-bold text-emerald-900">Препоръчителни храни</h4>
                </div>
                <div className="flex flex-wrap gap-1">
                  {data.food_recommendations.top.map((f, i) => (
                    <span key={i} className="text-[10.5px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-medium">
                      {f.name_bg}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50/40">
              <CardContent className="p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <Leaf className="w-3.5 h-3.5 text-amber-600" />
                  <h4 className="text-xs font-bold text-amber-900">По-рядко</h4>
                </div>
                <div className="flex flex-wrap gap-1">
                  {data.food_recommendations.limit.map((f, i) => (
                    <span key={i} className="text-[10.5px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
                      {f.name_bg}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Disclaimer */}
        <Card className="border-amber-200 bg-amber-50/60">
          <CardContent className="p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] leading-relaxed text-amber-900">{data.disclaimer_bg}</p>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center pt-2">
          <p className="text-[10px] text-slate-500">
            <ShieldCheck className="w-3 h-3 inline mr-0.5" />
            Powered by Nutramilo · Преглеждания: {meta.view_count}
          </p>
        </div>
      </div>
    </div>
  );
}
